// Helm / seating configuration — structured production fields + a deterministic parsing rule
// for legacy commercial descriptions. Production surfaces (Assembly export, preview, revision
// diff, notifications) show these structured values; the Sales Configurator keeps the full text.
//
// Priority (see resolveHelm): approved structured catalogue fields → approved parsing rule →
// original description. ML may SUGGEST structured values from text, but a human approves them
// before use (structured_approved) and it never rewrites a confirmed order.
const WORD_NUM = { one: 1, two: 2, three: 3, four: 4 };
/** True if a catalogue item name looks like a helm/seating configuration option. */
export function isHelmOption(name) {
    return !!name && /helm\s+bucket\s+seats|rows?\s+of\s+\d+\s*[x×]/i.test(name);
}
/** Deterministic parsing rule: extract structured seating fields from a description. */
export function parseHelmOption(name) {
    if (!name || !isHelmOption(name))
        return null;
    const rowsM = name.match(/\b(one|two|three|four|\d+)\s+rows?\b/i);
    const seatsM = name.match(/(\d+)\s*[x×]\s*helm\s+bucket\s+seats/i);
    const seatingRows = rowsM ? WORD_NUM[rowsM[1].toLowerCase()] ?? Number(rowsM[1]) : 1;
    const seatsPerRow = seatsM ? Number(seatsM[1]) : 0;
    const aftConfiguration = /wet\s*bar/i.test(name)
        ? 'Wet Bar'
        : /back[-\s]?to[-\s]?back/i.test(name)
            ? 'Back-to-Back'
            : null;
    const fridgeM = name.match(/(\d+)\s*[x×]\s*(\d+)\s*L\b/i);
    const fridgeQuantity = fridgeM ? Number(fridgeM[1]) : null;
    const fridgeCapacityLitres = fridgeM ? Number(fridgeM[2]) : null;
    const compatibilityNote = /available only with[^)]*i-?\s?shaped/i.test(name)
        ? 'Requires I-shaped forward-facing aft seating'
        : null;
    return { seatingRows, seatsPerRow, aftConfiguration, fridgeQuantity, fridgeCapacityLitres, compatibilityNote };
}
/** Required fields for a helm option (used for validation / approval). */
export function validateHelmStructured(hs) {
    const missing = [];
    if (!hs || !hs.seatingRows)
        missing.push('seatingRows');
    if (!hs || !hs.seatsPerRow)
        missing.push('seatsPerRow');
    return missing;
}
/** Seats display: "3" for one row, "3 + 3" for two rows, etc. */
function seatsDisplay(hs) {
    const rows = Math.max(1, hs.seatingRows || 1);
    return Array.from({ length: rows }, () => hs.seatsPerRow).join(' + ');
}
/** Multi-line structured value for the Assembly export cell. */
export function formatHelmExport(hs) {
    const lines = [`Rows: ${hs.seatingRows}`, `Seats: ${seatsDisplay(hs)}`];
    if (hs.aftConfiguration)
        lines.push(`Aft configuration: ${hs.aftConfiguration}`);
    if (hs.fridgeQuantity && hs.fridgeCapacityLitres)
        lines.push(`Fridges: ${hs.fridgeQuantity} x ${hs.fridgeCapacityLitres}L`);
    if (hs.compatibilityNote)
        lines.push(`Compatibility: ${hs.compatibilityNote}`);
    return lines.join('\n');
}
/** Single-line concise form for preview / revision diff / notifications. */
export function formatHelmShort(hs) {
    const parts = [`${hs.seatingRows} row${hs.seatingRows > 1 ? 's' : ''} × ${seatsDisplay(hs)}`];
    if (hs.aftConfiguration)
        parts.push(hs.aftConfiguration);
    if (hs.fridgeQuantity && hs.fridgeCapacityLitres)
        parts.push(`${hs.fridgeQuantity}×${hs.fridgeCapacityLitres}L`);
    let s = parts.join(' · ');
    if (hs.compatibilityNote)
        s += ` (${hs.compatibilityNote})`;
    return s;
}
/**
 * Resolve the structured seating fields to use, by priority:
 * 1) approved structured catalogue fields, 2) the parsing rule on the name, 3) null (→ description).
 */
export function resolveHelm(item) {
    if (item.structured && item.structured.seatingRows)
        return item.structured;
    return parseHelmOption(item.name);
}
//# sourceMappingURL=helm.js.map